import React, { useState, useRef, useCallback, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import {
  UserCircleIcon,
  HandshakeIcon,
  UsersIcon,
  MegaphoneIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  RobotIcon,
  LinkedinLogoIcon,
  CursorClickIcon,
  NewspaperIcon,
  DotsThreeOutlineIcon,
  TrophyIcon,
} from "@phosphor-icons/react";
import { Navigate, useNavigate } from "react-router-dom";
import { Typography } from "antd";
import { Grid, GridItem, Stack, radius } from "@libertify/ui";
import { useAntTheme } from "hooks/useAntTheme/";
import { useCreate, useNotification, useTranslate } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner } from "components/LoadingSpinner";
import { useHasCompletedQuestionnaire } from "hooks/useHasCompletedQuestionnaire";
import { OnboardingAnswer } from "pages/media/types";
import mainBg from "@/assets/main-background.png";

const LIBERTIFY_LOGO_URL = "/images/Isologo-regular.svg";
// TODO: revisit later when the design system is implemented
const PRIMARY_COLOR = "#14b8a6";
const PRIMARY_COLOR_RGB = "20, 184, 166";
const PRIMARY_COLOR_DARK = "#0d9488";
const PRIMARY_COLOR_LIGHT = "#ccfbf1";
const SURFACE_COLOR = "#ffffff";
const TEXT_SECONDARY_COLOR = "#6b7280";

const STEPS = ["useCase", "teamSize", "hearAboutUs"] as const;
type Step = (typeof STEPS)[number];
const stepIndex = (step: Step) => STEPS.indexOf(step);
type UseCase = "myself" | "clients" | "team" | "audience";
type TeamSize = "solo" | "2-10" | "11-50" | "50+";
type HearAboutUs =
  | "award"
  | "google"
  | "ai"
  | "linkedin"
  | "influencer"
  | "ad"
  | "blog"
  | "other";

interface Answers {
  useCase: UseCase | null;
  teamSize: TeamSize | null;
  hearAboutUs: HearAboutUs | null;
}

const USE_CASE_OPTIONS: {
  value: UseCase;
  Icon: React.ElementType;
}[] = [
  { value: "myself", Icon: UserCircleIcon },
  { value: "clients", Icon: HandshakeIcon },
  { value: "team", Icon: UsersIcon },
  { value: "audience", Icon: MegaphoneIcon },
];

const TEAM_SIZE_OPTIONS: { value: TeamSize }[] = [
  { value: "solo" },
  { value: "2-10" },
  { value: "11-50" },
  { value: "50+" },
];

const HEAR_ABOUT_US_OPTIONS: {
  value: HearAboutUs;
  Icon: React.ElementType;
}[] = [
  { value: "award", Icon: TrophyIcon },
  { value: "google", Icon: MagnifyingGlassIcon },
  { value: "ai", Icon: RobotIcon },
  { value: "linkedin", Icon: LinkedinLogoIcon },
  { value: "influencer", Icon: MegaphoneIcon },
  { value: "ad", Icon: CursorClickIcon },
  { value: "blog", Icon: NewspaperIcon },
  { value: "other", Icon: DotsThreeOutlineIcon },
];

const slideInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

const slideOutUp = keyframes`
  from { opacity: 1; transform: translateY(0);     }
  to   { opacity: 0; transform: translateY(-14px); }
`;

export const OnboardingQuestionnaire: React.FC = () => {
  const navigate = useNavigate();
  const t = useTranslate();
  const { isMobile, theme } = useAntTheme();
  const { shouldShowQuestionnaire, isLoading: isLoadingQuestionnaire } =
    useHasCompletedQuestionnaire();

  const [displayStep, setDisplayStep] = useState<Step>("useCase");
  const [isExiting, setIsExiting] = useState(false);
  const [answers, setAnswers] = useState<Answers>({
    useCase: null,
    teamSize: null,
    hearAboutUs: null,
  });
  const isSubmittingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    },
    []
  );

  const { mutateAsync: createAnswer } = useCreate<OnboardingAnswer>();
  const queryClient = useQueryClient();
  const { open: openNotification } = useNotification();

  const goTo = useCallback(
    (next: Step) => {
      if (isExiting) return;
      setIsExiting(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
      timerRef.current = setTimeout(() => {
        setDisplayStep(next);
        setIsExiting(false);
      }, 270);
    },
    [isExiting]
  );

  const finish = useCallback(
    async (override?: Partial<Answers>) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      const finalAnswers: Answers = { ...answers, ...override };

      const payloads: Array<{
        question: string;
        answer: Record<string, unknown>;
      }> = [];
      if (finalAnswers.useCase) {
        payloads.push({
          question: "USE_CASE",
          answer: { answers: finalAnswers.useCase },
        });
      }
      if (finalAnswers.teamSize) {
        payloads.push({
          question: "TEAM_SIZE",
          answer: { answers: finalAnswers.teamSize },
        });
      }
      if (finalAnswers.hearAboutUs) {
        payloads.push({
          question: "HEAR_ABOUT_US",
          answer: { answers: finalAnswers.hearAboutUs },
        });
      }

      try {
        await Promise.all(
          payloads.map((values) =>
            createAnswer({
              resource: "media/onboarding/answer",
              values,
              successNotification: false,
            })
          )
        );
        // Force a refetch and AWAIT it before navigating. invalidate() alone
        // just marks the cache stale; useList in OnboardingDashboard would
        // read the stale (empty) data on its first render and bounce the
        // user back to /questionnaire before the background refetch lands.
        //
        // dataUpdatedAt > 0 skips phantom cache entries created when
        // useHasCompletedQuestionnaire first mounts with userId=undefined
        // (queryOptions.enabled is false then, but the key is still
        // registered). Refetching one of those fires a request with no
        // user_id and the backend returns 422.
        await queryClient.refetchQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey.includes("media/onboarding/answer") &&
            query.state.dataUpdatedAt > 0,
        });
      } catch {
        isSubmittingRef.current = false;
        openNotification?.({
          type: "error",
          message: "",
          description: t("pages.onboarding.questionnaire.errors.submitFailed"),
        });
        return;
      }

      navigate("/home");
    },
    [answers, createAnswer, navigate, queryClient, openNotification, t]
  );

  const selectUseCase = useCallback(
    (value: UseCase) => {
      if (isExiting) return;
      setAnswers((a) => ({ ...a, useCase: value }));
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
      autoTimerRef.current = setTimeout(() => goTo("teamSize"), 350);
    },
    [isExiting, goTo]
  );

  const selectTeamSize = useCallback(
    (value: TeamSize) => {
      if (isExiting) return;
      setAnswers((a) => ({ ...a, teamSize: value }));
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
      autoTimerRef.current = setTimeout(() => goTo("hearAboutUs"), 350);
    },
    [isExiting, goTo]
  );

  const selectHearAboutUs = useCallback(
    (value: HearAboutUs) => {
      if (isExiting) return;
      setAnswers((a) => ({ ...a, hearAboutUs: value }));
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
      // Pass override — finish() captures `answers` from render closure and
      // wouldn't see the value we just set otherwise.
      autoTimerRef.current = setTimeout(() => {
        finish({ hearAboutUs: value });
      }, 350);
    },
    [isExiting, finish]
  );

  const handleSkip = useCallback(() => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    const next = STEPS[stepIndex(displayStep) + 1];
    if (next) {
      goTo(next);
      return;
    }
    finish();
  }, [displayStep, goTo, finish]);

  const handleBack = useCallback(() => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    const prev = STEPS[stepIndex(displayStep) - 1];
    if (prev) goTo(prev);
  }, [displayStep, goTo]);

  if (isLoadingQuestionnaire) {
    return <LoadingSpinner />;
  }

  // Covers both the already-answered case and teacher orgs landing here
  // through direct navigation.
  if (!shouldShowQuestionnaire) {
    return <Navigate to="/home" replace />;
  }

  return (
    <PageBackground direction="vertical">
      <HeaderRow $isMobile={isMobile} align="center" justify="between">
        <img
          src={LIBERTIFY_LOGO_URL}
          alt={t("pages.onboarding.questionnaire.logoAlt")}
          style={{ height: 28, width: "auto", display: "block" }}
        />
      </HeaderRow>

      <MainContent
        $isMobile={isMobile}
        align={isMobile ? "start" : "center"}
        justify="center"
      >
        <div style={{ width: "100%", maxWidth: 560 }}>
          <ProgressBarRow gap={5}>
            {STEPS.map((s) => (
              <div
                key={s}
                style={{
                  flex: 1,
                  height: 3,
                  borderRadius: radius.pill,
                  background:
                    stepIndex(s) <= stepIndex(displayStep)
                      ? PRIMARY_COLOR
                      : "rgba(0, 0, 0, 0.12)",
                  transition: "background 0.4s ease",
                }}
              />
            ))}
          </ProgressBarRow>
          <StepInfoRow align="center" justify="between">
            <BackButton
              type="button"
              $visible={stepIndex(displayStep) > 0}
              onClick={handleBack}
            >
              <ArrowLeftIcon size={14} />
              {t("pages.onboarding.questionnaire.back")}
            </BackButton>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: theme.colorTextSecondary,
                background: "rgba(0, 0, 0, 0.07)",
                borderRadius: radius.pill,
                padding: "3px 10px",
              }}
            >
              {t("pages.onboarding.questionnaire.stepIndicator", {
                current: stepIndex(displayStep) + 1,
                total: STEPS.length,
              })}
            </span>
          </StepInfoRow>
          <Typography.Title
            level={2}
            style={{
              fontFamily: '"Poppins", sans-serif',
              fontSize: "clamp(24px, 4vw, 32px)",
              fontWeight: 700,
              letterSpacing: "-0.025em",
              lineHeight: 1.2,
              marginTop: 0,
              marginBottom: 10,
            }}
          >
            {t(`pages.onboarding.questionnaire.steps.${displayStep}.question`)}
          </Typography.Title>
          <Typography.Text
            type="secondary"
            style={{
              display: "block",
              marginBottom: 24,
            }}
          >
            {t(`pages.onboarding.questionnaire.steps.${displayStep}.subtext`)}
          </Typography.Text>

          <QuestionBlock key={displayStep} $exiting={isExiting}>
            {displayStep === "useCase" && (
              <UseCaseGrid columns={12} gap={10}>
                {USE_CASE_OPTIONS.map(({ value, Icon }) => {
                  const selected = answers.useCase === value;
                  return (
                    <GridItem key={value} span={12} smSpan={6}>
                      <OptionTile
                        type="button"
                        $selected={selected}
                        onClick={() => selectUseCase(value)}
                      >
                        {selected && <TileCheckedIndicator />}
                        <TileIcon selected={selected} icon={Icon} />
                        <TileLabel>
                          {t(
                            `pages.onboarding.questionnaire.useCase.${value}.label`
                          )}
                        </TileLabel>
                        <TileSub>
                          {t(
                            `pages.onboarding.questionnaire.useCase.${value}.sub`
                          )}
                        </TileSub>
                      </OptionTile>
                    </GridItem>
                  );
                })}
              </UseCaseGrid>
            )}

            {displayStep === "teamSize" && (
              <TeamSizeRow wrap="wrap" gap={8}>
                {TEAM_SIZE_OPTIONS.map(({ value }) => (
                  <OptionPill
                    key={value}
                    type="button"
                    $selected={answers.teamSize === value}
                    onClick={() => selectTeamSize(value)}
                  >
                    {t(`pages.onboarding.questionnaire.teamSize.${value}`)}
                  </OptionPill>
                ))}
              </TeamSizeRow>
            )}

            {displayStep === "hearAboutUs" && (
              <PSWideGrid>
                {HEAR_ABOUT_US_OPTIONS.map(({ value, Icon }) => {
                  const selected = answers.hearAboutUs === value;
                  return (
                    <OptionTile
                      key={value}
                      type="button"
                      $selected={selected}
                      onClick={() => selectHearAboutUs(value)}
                    >
                      {selected && <TileCheckedIndicator />}
                      <TileIcon selected={selected} icon={Icon} />
                      <TileLabel>
                        {t(
                          `pages.onboarding.questionnaire.hearAboutUs.${value}`
                        )}
                      </TileLabel>
                    </OptionTile>
                  );
                })}
              </PSWideGrid>
            )}
            {displayStep !== "useCase" && (
              <Stack align="center" justify="end" gap={12}>
                <SkipButton type="button" onClick={handleSkip}>
                  {t("pages.onboarding.questionnaire.skip")}
                </SkipButton>
              </Stack>
            )}
          </QuestionBlock>
        </div>
      </MainContent>
    </PageBackground>
  );
};

const TileIcon = ({
  selected,
  icon: Icon,
}: {
  selected: boolean;
  icon: React.ElementType;
}) => {
  const { theme } = useAntTheme();
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: selected
          ? `rgba(${PRIMARY_COLOR_RGB}, 0.16)`
          : theme.colorLightGrey,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background 0.15s",
      }}
    >
      <Icon size={18} color={selected ? PRIMARY_COLOR : TEXT_SECONDARY_COLOR} />
    </div>
  );
};

const TileCheckedIndicator = () => (
  <span
    style={{
      position: "absolute",
      top: 10,
      right: 10,
    }}
  >
    <CheckCircleIcon size={16} color={PRIMARY_COLOR} weight="fill" />
  </span>
);

const PageBackground = styled(Stack)`
  min-height: 100vh;
  background-image: url(${mainBg});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
`;

const HeaderRow = styled(Stack)<{ $isMobile: boolean }>`
  padding: ${(p) => (p.$isMobile ? "18px 16px" : "22px 32px")};
`;

const MainContent = styled(Stack)<{ $isMobile: boolean }>`
  flex: 1;
  padding: ${(p) => (p.$isMobile ? "28px 16px 64px" : "40px 32px 72px")};
`;

const ProgressBarRow = styled(Stack)`
  margin-bottom: 20px;
`;

const StepInfoRow = styled(Stack)`
  margin-bottom: 22px;
`;

const UseCaseGrid = styled(Grid)`
  margin-bottom: 24px;
`;

const TeamSizeRow = styled(Stack)`
  margin-bottom: 24px;
`;

const QuestionBlock = styled.div<{ $exiting: boolean }>`
  animation: ${(p) => (p.$exiting ? slideOutUp : slideInUp)}
    ${(p) => (p.$exiting ? "0.26s" : "0.36s")} ease
    ${(p) => (p.$exiting ? "forwards" : "both")};
`;

const PSWideGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 24px;

  @media (width <= 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const selectableBaseStyles = css<{ $selected: boolean }>`
  height: auto;
  border: 1.5px solid ${(p) => (p.$selected ? PRIMARY_COLOR : "#e5e7eb")};
  transition:
    border-color 0.15s,
    background 0.15s;

  &:hover {
    border-color: ${PRIMARY_COLOR};
  }
`;

const OptionTile = styled.button<{ $selected: boolean }>`
  ${selectableBaseStyles};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 6px;
  padding: 16px;
  border-radius: ${radius.box}px;
  background: ${(p) => (p.$selected ? PRIMARY_COLOR_LIGHT : SURFACE_COLOR)};
  box-shadow: ${(p) =>
    p.$selected
      ? `0 4px 14px -4px rgba(${PRIMARY_COLOR_RGB}, 0.28)`
      : "0 1px 4px -2px rgba(0, 0, 0, 0.08)"};
  text-align: left;
  white-space: normal;
  cursor: pointer;
  font: inherit;
  transition:
    border-color 0.15s,
    background 0.15s,
    box-shadow 0.15s,
    transform 0.15s;

  &:hover {
    transform: ${(p) => (p.$selected ? "none" : "translateY(-2px)")};
    box-shadow: 0 4px 14px -4px rgba(${PRIMARY_COLOR_RGB}, 0.22);
    background: ${(p) => (p.$selected ? PRIMARY_COLOR_LIGHT : SURFACE_COLOR)};
  }
`;

const OptionPill = styled.button<{ $selected: boolean }>`
  ${selectableBaseStyles};
  padding: 9px 20px;
  border-radius: ${radius.pill}px;
  background: ${(p) => (p.$selected ? PRIMARY_COLOR : SURFACE_COLOR)};
  color: ${(p) => (p.$selected ? SURFACE_COLOR : "#374151")};
  font-size: 14px;
  font-weight: ${(p) => (p.$selected ? 600 : 400)};
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;
  transition:
    border-color 0.15s,
    background 0.15s,
    color 0.15s;

  &:hover {
    background: ${(p) =>
      p.$selected ? PRIMARY_COLOR_DARK : `rgba(${PRIMARY_COLOR_RGB}, 0.06)`};
    color: ${(p) => (p.$selected ? SURFACE_COLOR : PRIMARY_COLOR_DARK)};
  }
`;

const TileLabel = styled(Typography.Text)`
  font-weight: 600;
  line-height: 1.3;
`;

const TileSub = styled(Typography.Text)`
  font-size: 11px;
  color: ${TEXT_SECONDARY_COLOR};
  line-height: 1.4;
`;

const BackButton = styled.button<{ $visible: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT_SECONDARY_COLOR};
  cursor: pointer;
  padding: 0;
  visibility: ${(p) => (p.$visible ? "visible" : "hidden")};
  transition: color 0.15s;

  &:hover {
    color: #111827;
  }
`;

const SkipButton = styled.button`
  height: 44px;
  padding: 0 24px;
  border: 1.5px solid #d1d5db;
  border-radius: ${radius.pill}px;
  background: transparent;
  font: inherit;
  font-weight: 600;
  color: ${TEXT_SECONDARY_COLOR};
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s;

  &:hover {
    border-color: ${PRIMARY_COLOR};
    color: ${PRIMARY_COLOR_DARK};
  }
`;
